import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowId,
  GridRowModel,
  useGridApiContext,
} from "@mui/x-data-grid";
import { memo, ReactNode, useCallback, useMemo, useState } from "react";
import {
  ContactTableItemFragment,
  SupplierTableItemFragment,
  useContactUpsertMutation,
} from "../../../__generated__/types-and-hooks";
import { useTeam } from "../TeamLayout";
import { useTranslation } from "../../../i18n";
import { useNotify } from "../../../hooks/useNotify";
import ContactRef from "../../../components/ContactRef";
import { Box, Chip, IconButton, Tooltip } from "@mui/joy";
import Email from "@mui/icons-material/EmailOutlined";
import WhatsApp from "@mui/icons-material/WhatsApp";
import Phone from "@mui/icons-material/LocalPhone";
import Message from "@mui/icons-material/Message";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import ContactMethodsModal from "./EditContactMethodsModal";
import { MultiSupplierAutocomplete } from "../../../components/SupplierAutocomplete";
import { CountryFlag } from "../../../components/CountryFlag";
import GridStyles from "../../../components/datagrid/GridStyles";
import FactoryIcon from "@mui/icons-material/Factory";
import SupplierTooltip from "../inspections/views/SupplierTooltip";

export interface TableViewProps {
  contacts: ContactTableItemFragment[];
  onUpdate?: () => void;
  loading?: boolean;
}

// Map of contact method keys to corresponding icons
const iconMap: Record<string, ReactNode> = {
  phone: <Phone style={{ fontSize: 14 }} />,
  email: <Email style={{ fontSize: 14 }} />,
  whatsapp: <WhatsApp style={{ fontSize: 14 }} />,
};

export interface ContactMethodsProps {
  contact: ContactTableItemFragment;
  onEditClick: (contact: ContactTableItemFragment) => void;
}

const ContactMethods = memo(function ContactMethods({
  contact,
  onEditClick,
}: ContactMethodsProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "nowrap",
        alignContent: "space-between",
        gap: 0.5,
      }}
    >
      <Box sx={{ overflow: "hidden", flex: 1 }}>
        {contact.contactMethods.map((method, index) => (
          <Chip
            key={index}
            variant="soft"
            size={"sm"}
            sx={{ mr: 1, borderRadius: 4 }}
            startDecorator={
              iconMap[method.key] ?? <Message style={{ fontSize: 14 }} />
            }
          >
            {method.value}
          </Chip>
        ))}
      </Box>
      <IconButton
        sx={{ fontSize: 12 }}
        size="sm"
        color="primary"
        variant="plain"
        onClick={() => onEditClick(contact)}
      >
        <ModeEditIcon style={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
});

export default function TableView({
  contacts,
  onUpdate,
  loading,
}: TableViewProps) {
  const t = useTranslation();
  const teamCtx = useTeam();
  const team = teamCtx.team;
  const notify = useNotify();
  const [contactUpsertMutation] = useContactUpsertMutation({});

  const [contactMethodsOpen, setContactMethodsOpen] =
    useState<ContactTableItemFragment | null>(null);
  const handleContactMethodsEditClick = useCallback(
    (contact: ContactTableItemFragment) => {
      setContactMethodsOpen(contact);
    },
    []
  );

  function RefRenderCell(params: GridRenderCellParams<any, string>) {
    return <ContactRef contactRef={params.row.uniqueId} teamSlug={team.slug} />;
  }

  function ContactMethodsRenderCell(params: GridRenderCellParams<any, string>) {
    return (
      <ContactMethods
        onEditClick={handleContactMethodsEditClick}
        contact={params.row}
      />
    );
  }

  function SupplierRenderCell(params: GridRenderCellParams<any, any>) {
    return (
      <>
        {(params.row.suppliers ?? []).map((supplier: any) => (
          <Tooltip
            enterDelay={700}
            leaveDelay={200}
            key={supplier.uniqueId}
            variant="outlined"
            arrow
            title={<SupplierTooltip teamSlug={team.slug} supplier={supplier} />}
          >
            <Chip
              variant="soft"
              sx={{ borderRadius: 4, fontSize: 12 }}
              startDecorator={<FactoryIcon style={{ fontSize: 12 }} />}
            >
              {supplier.name}
            </Chip>
          </Tooltip>
        ))}
      </>
    );
  }

  function CountryRenderCell(params: GridRenderCellParams<any, any>) {
    return (
      params.row.countryCode && <CountryFlag code={params.row.countryCode} />
    );
  }

  function SupplierRenderEditCell(props: GridRenderEditCellParams) {
    const { id, value, field, hasFocus } = props;
    const apiRef = useGridApiContext();

    return (
      <MultiSupplierAutocomplete
        hasFocus={hasFocus}
        teamId={team.id}
        suppliers={value ?? []}
        onChange={async (suppliers) => {
          await apiRef.current.setEditCellValue({
            id,
            field,
            value: suppliers ?? [],
          });
          await apiRef.current.stopCellEditMode({ id, field });
        }}
      />
    );
  }

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "uniqueId",
        headerName: t("ID"),
        width: 80,
        editable: false,
        renderCell: RefRenderCell,
      },
      {
        field: "name",
        headerName: t("Full Name"),
        width: 200,
        editable: true,
      },
      {
        field: "contactMethods",
        headerName: t("Contact Methods"),
        width: 360,
        editable: false,
        renderCell: ContactMethodsRenderCell,
      },
      {
        field: "suppliers",
        headerName: t("Suppliers"),
        width: 300,
        editable: true,
        renderCell: SupplierRenderCell,
        renderEditCell: SupplierRenderEditCell,
      },
      {
        field: "addressLine1",
        headerName: t("Address Line 1"),
        width: 200,
        editable: true,
      },
      {
        field: "addressLine2",
        headerName: t("Address Line 2"),
        width: 120,
        editable: true,
      },
      {
        field: "city",
        headerName: t("City"),
        width: 100,
        editable: true,
      },
      {
        field: "state",
        headerName: t("State"),
        width: 100,
        editable: true,
      },
      {
        field: "postalCode",
        headerName: t("Zip Code"),
        width: 100,
        editable: true,
      },
      {
        field: "countryCode",
        headerName: t("Country"),
        width: 100,
        editable: true,
        renderCell: CountryRenderCell,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  const handleProcessRowUpdate = useCallback(
    async (
      newRow: GridRowModel,
      oldRow: GridRowModel,
      { rowId }: { rowId: GridRowId }
    ) => {
      const delta = (field: string, key?: string) =>
        newRow[field] !== oldRow[field]
          ? typeof key === "string" && typeof newRow[field] === "object"
            ? newRow[field][key]
            : newRow[field]
          : void 0;

      const res = await contactUpsertMutation({
        variables: {
          contact: {
            teamId: teamCtx.team.id,
            id: rowId as number,
            name: delta("name"),
            addressLine1: delta("addressLine1"),
            addressLine2: delta("addressLine2"),
            city: delta("city"),
            state: delta("state"),
            postalCode: delta("postalCode"),
            countryCode: delta("countryCode"),
            suppliers:
              delta("suppliers")?.map((s: SupplierTableItemFragment) => s.id) ??
              void 0,
          },
        },
      });
      if (res.errors) {
        throw new Error(res.errors.map((e) => e.message).join("\n"));
      }
      return newRow;
    },
    [contactUpsertMutation, teamCtx]
  );

  return (
    <>
      <DataGrid
        {...GridStyles}
        loading={loading}
        rows={contacts}
        columns={columns}
        editMode="cell"
        disableRowSelectionOnClick
        processRowUpdate={handleProcessRowUpdate}
        onProcessRowUpdateError={(err) => {
          notify({
            type: "danger",
            msg: err.message,
          });
        }}
      />
      {contactMethodsOpen && (
        <ContactMethodsModal
          contact={contactMethodsOpen}
          onClose={() => {
            if (onUpdate) {
              onUpdate();
            }
            setContactMethodsOpen(null);
          }}
        />
      )}
    </>
  );
}
